import { AmorgAdminToken, AmorgAdminUserListing, ApplicationFeatures, DealFlags, GlobalSurveyTemplate, LoggedEmail, OrganizationFlags, Partner, PublicAmorgUser, CompanyInfo, apiPath, SiteFlags, SiteView, OrganizationListView, OrganizationView, OrganizationContact, OrganizationDeal, OrganizationNote, AmorgAdminHttpCommandResult, UserImportPreviewResult } from '../amorg-common';
import { SiteSettings } from '../amorg-common';
import { appUrlBuilder } from '../utils/appUrlBuilder';
import { getAuthorizedActiveUserOrRedirect } from './serverApiHelpers';

export const amorgAdminPath = `${apiPath}/amorgy`;

export async function getEmails (siteId: string, onNoAccess: () => void): Promise<LoggedEmail[]>
{
    const response = await fetch(`${amorgAdminPath}/email-log/${siteId}`);
    if (response.status === 401)
    {
        onNoAccess();
        return [];
    }
    else if (response.status === 200)
        return response.json();
    else
        throw new Error('Unhandled result');
}

export async function getAllSites (onNoAccess: () => void): Promise<SiteView[]>
{
    const response = await fetch(`${amorgAdminPath}/sites`);
    if (response.status === 401)
    {
        onNoAccess();
        return [];
    }
    else if (response.status === 200)
    {
        const siteList = await response.json() as SiteView[];
        siteList.sort((lhs, rhs) => lhs.id.localeCompare(rhs.id));
        return siteList;
    }
    else
        throw new Error('Unhandled result');
}



export async function getSiteById (siteId: string, onNoAccess: () => void): Promise<SiteView | null>
{
    const response = await fetch(`${amorgAdminPath}/sites/${siteId}`);
    if (response.status === 401)
    {
        onNoAccess();
        return null;
    }
    else if (response.status === 200)
    {
        return await response.json() as SiteView;
    }
    else
        throw new Error('Unhandled result');
}

export async function getSiteUsers (siteId: string, onNoAccess: () => void): Promise<PublicAmorgUser[]>
{
    const response = await fetch(`${amorgAdminPath}/sites/${siteId}/users`);
    if (response.status === 401)
    {
        onNoAccess();
        return [];
    }
    else if (response.status === 200)
    {
        const users = await response.json() as PublicAmorgUser[];
        users.sort((lhs, rhs) => lhs.email.localeCompare(rhs.email));
        return users;
    }
    else
        throw new Error('Unhandled result');
}

export type SiteScriptListItem = { id: string, name: string, description: string; };

export async function getSiteScripts (onNoAccess: () => void): Promise<SiteScriptListItem[]>
{
    const response = await fetch(`${amorgAdminPath}/site-scripts`);
    if (response.status === 401)
    {
        onNoAccess();
        return [];
    }
    else if (response.status === 200)
        return response.json();
    else
        throw new Error('Unhandled result');
}

export type SiteScriptRunResult =
    { status: 'success'; } |
    { status: 'failure', clientMessage: string; };

export async function runSiteScript (siteId: string, scriptId: string, onNoAccess: () => void): Promise<SiteScriptRunResult>
{
    const response = await fetch(`${amorgAdminPath}/sites/${siteId}/site-scripts/run`,
        {
            method: 'POST',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ scriptId })
        });
    if (response.status === 401)
    {
        onNoAccess();
        return { status: 'failure', clientMessage: 'not authorized' };
    }
    else if (response.status === 200 || response.status === 400)
        return response.json();
    else
        return { status: 'failure', clientMessage: 'Unhandled result' };
}

export async function impersonate (siteId: string, email: string): Promise<{ landingUrl:string }>
{
    const response = await fetch(`${amorgAdminPath}/sites/${siteId}/users/${email}/impersonate`,
        {
            method: 'POST',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' }
        });
    if (response.status === 401)
        return { landingUrl: '/amorgy/login' };
    else if (response.status === 200)
    {
        const user = await getAuthorizedActiveUserOrRedirect([]);
        return { landingUrl: appUrlBuilder.home.build(user) };
    }
    else
        return { landingUrl: '/amorgy/login' };
}

export async function createSite (siteId: string, organizationId: string, onNoAccess: () => void): Promise<boolean>
{
    const response = await fetch(`/api/amorgy/sites`,
        {
            method: 'POST',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: `{"siteId":"${siteId}", "organizationId":"${organizationId}"}`
        });
    if (response.status === 401)
    {
        onNoAccess();
        return false;
    }
    else if (response.status === 200)
    {
        return true;
    }
    else
        return false;
}

export async function updateSite (siteId: string, organizationId: string, settings: SiteSettings, loginName:string, onNoAccess: () => void): Promise<boolean>
{
    settings.features = settings.features.filter(f => ApplicationFeatures.includes(f));
    settings.flags = (settings.flags || []).filter(f => SiteFlags.includes(f));

    const response = await fetch(`/api/amorgy/sites/${siteId}`,
        {
            method: 'PUT',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ organizationId, ...settings, loginName })
        });
    if (response.status === 401)
    {
        onNoAccess();
        return false;
    }
    else if (response.status === 200)
        return true;
    else
    {
        console.log(`Failed to update site ${siteId}. Status: ${response.status}`);
        return false;
    }
}

// Automatically filters by partner if the active user is a partner user
export async function getOrganizations (onNoAccess: () => void): Promise<OrganizationListView[]>
{
    const response = await fetch(`/api/amorgy/organizations`,
        {
            method: 'GET',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' }
        });
    if (response.status === 401)
    {
        onNoAccess();
        return [];
    }
    else if (response.status === 200)
    {
        const orgList = await response.json() as OrganizationListView[];
        return orgList;
    }
    else
        throw new Error('Unhandled result');
}

export async function getOrganizationById (organizationId: string, onNoAccess: () => void): Promise<OrganizationView | null>
{
    const response = await fetch(`/api/amorgy/organizations/${organizationId}`, 
        {
            method: 'GET',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' }
        });
    if (response.status === 401)
    {
        onNoAccess();
        return null;
    }
    else if (response.status === 200)
    {
        const org = await response.json() as OrganizationView;
        return {
            ...org,
            deals: org.deals.map(mapOrganizationDealFromApi)
        };
    }
    else
        throw new Error('Unhandled result');
}

export async function getOrganizationBySiteId (siteId: string, onNoAccess: () => void): Promise<OrganizationView | null>
{
    const response = await fetch(`/api/amorgy/sites/${siteId}/organization`,
        {
            method: 'GET',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' }
        });
    if (response.status === 401)
    {
        onNoAccess();
        return null;
    }
    else if (response.status === 200)
    {
        const org = await response.json() as OrganizationView;
        return {
            ...org,
            deals: org.deals.map(mapOrganizationDealFromApi)
        };
    }
    else
        throw new Error('Unhandled result');
}

export async function createOrganization (organizationId: string, onNoAccess: () => void): Promise<AmorgAdminHttpCommandResult>
{
    const response = await fetch(`/api/amorgy/organizations`,
        {
            method: 'POST',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: `{"organizationId":"${organizationId}"}`
        });
    if (response.status === 401)
    {
        onNoAccess();
        return { status: 'failure', clientMessage: 'No access' };
    }
    else if(response.status === 400 || response.status === 200)
    {
        const result = JSON.parse(await response.text());
        console.log(result);
        return result;
    }
    else
        return { status: 'failure', clientMessage: 'Unhandled result' };
}

export async function updateOrganization (organizationId: string, companyInfo: CompanyInfo, partner:Partner|null, onNoAccess: () => void): Promise<boolean>
{
    companyInfo.flags = (companyInfo.flags || []).filter(f => OrganizationFlags.includes(f));

    const response = await fetch(`/api/amorgy/organizations/${organizationId}`,
        {
            method: 'PUT',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ companyInfo, partner })
        });
    if (response.status === 401)
    {
        onNoAccess();
        return false;
    }
    else if (response.status === 200)
        return true;
    else
        return false;   
}

export async function getOrganizationContacts (organizationId: string, onNoAccess: () => void): Promise<OrganizationContact[]>
{
    const response = await fetch(`/api/amorgy/organizations/${organizationId}/contacts`,
        {
            method: 'GET',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' }
        });
    if (response.status === 401)
    {
        onNoAccess();
        return [];
    }
    else if (response.status === 200)
    {
        return await response.json() as OrganizationContact[];
    }
    else
        throw new Error('Unhandled result');
}

export async function createOrganizationContact (
    organizationId: string,
    contact: Omit<OrganizationContact, 'id'>,
    onNoAccess: () => void
): Promise<boolean>
{
    const response = await fetch(`/api/amorgy/organizations/${organizationId}/contacts`,
        {
            method: 'POST',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(contact)
        });
    if (response.status === 401)
    {
        onNoAccess();
        return false;
    }
    else if (response.status === 200)
        return true;
    else
        return false;
}

export async function updateOrganizationContact (
    organizationId: string,
    contact: OrganizationContact,
    onNoAccess: () => void
): Promise<boolean>
{
    const response = await fetch(`/api/amorgy/organizations/${organizationId}/contacts/${contact.id}`,
        {
            method: 'PUT',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                name: contact.name,
                email: contact.email,
                phone: contact.phone,
                flags: contact.flags,
                note: contact.note
            })
        });
    if (response.status === 401)
    {
        onNoAccess();
        return false;
    }
    else if (response.status === 200)
        return true;
    else
        return false;
}

export async function deleteOrganizationContact (organizationId: string, contactId: string, onNoAccess: () => void): Promise<boolean>
{
    const response = await fetch(`/api/amorgy/organizations/${organizationId}/contacts/${contactId}`,
        {
            method: 'DELETE',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' }
        });
    if (response.status === 401)
    {
        onNoAccess();
        return false;
    }
    else if (response.status === 200)
        return true;
    else
        return false;
}

function mapOrganizationDealFromApi (deal:OrganizationDeal): OrganizationDeal
{
    return {
        ...deal,
        flags: deal.flags || [],
        start: deal.start ? new Date(deal.start) : null,
        end: deal.end ? new Date(deal.end) : null,
        payment: {
            ...deal.payment,
            reminderDate: deal.payment.reminderDate ? new Date(deal.payment.reminderDate) : null,
            payments: Array.isArray(deal.payment.payments)
                ? deal.payment.payments.map((payment) => ({
                    ...payment,
                    time: new Date(payment.time)
                }))
                : []
        }
    } satisfies OrganizationDeal;
}

export async function getOrganizationDeals (organizationId: string, onNoAccess: () => void): Promise<OrganizationDeal[]>
{
    const response = await fetch(`/api/amorgy/organizations/${organizationId}/deals`,
        {
            method: 'GET',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' }
        });
    if (response.status === 401)
    {
        onNoAccess();
        return [];
    }
    else if (response.status === 200)
    {
        const deals = await response.json() as OrganizationDeal[];
        return deals.map(mapOrganizationDealFromApi);
    }
    else
        throw new Error('Unhandled result');
}

export async function createOrganizationDeal (
    organizationId: string,
    deal: Omit<OrganizationDeal, 'id'>,
    onNoAccess: () => void
): Promise<boolean>
{
    deal.flags = (deal.flags || []).filter(f => DealFlags.includes(f));

    const response = await fetch(`/api/amorgy/organizations/${organizationId}/deals`,
        {
            method: 'POST',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(deal)
        });
    if (response.status === 401)
    {
        onNoAccess();
        return false;
    }
    else if (response.status === 200)
        return true;
    else
        return false;
}

export async function updateOrganizationDeal (
    organizationId: string,
    deal: OrganizationDeal,
    onNoAccess: () => void
): Promise<boolean>
{
    deal.flags = (deal.flags || []).filter(f => DealFlags.includes(f));

    const response = await fetch(`/api/amorgy/organizations/${organizationId}/deals/${deal.id}`,
        {
            method: 'PUT',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                title: deal.title,
                start: deal.start,
                end: deal.end,
                dealType: deal.dealType,
                flags: deal.flags,
                payment: deal.payment
            })
        });
    if (response.status === 401)
    {
        onNoAccess();
        return false;
    }
    else if (response.status === 200)
        return true;
    else
        return false;
}

export async function deleteOrganizationDeal (organizationId: string, dealId: string, onNoAccess: () => void): Promise<boolean>
{
    const response = await fetch(`/api/amorgy/organizations/${organizationId}/deals/${dealId}`,
        {
            method: 'DELETE',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' }
        });
    if (response.status === 401)
    {
        onNoAccess();
        return false;
    }
    else if (response.status === 200)
        return true;
    else
        return false;
}

export async function getGlobalSurveyTemplates (onNoAccess: () => void) : Promise<GlobalSurveyTemplate[]>
{
    const response = await fetch(`/api/amorgy/global-templates`,
        {
            method: 'GET',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' }
        });
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
        return (await response.json()) as GlobalSurveyTemplate[];
    else
        throw new Error('Unhandled result');

    return [];
}

export async function importGlobalSurveyTemplate (
    siteId: string,
    siteTemplateId: string,
    globalTemplateId: string,
    category: string,
    onNoAccess: () => void
)
{
    const body = JSON.stringify({ siteId, siteSurveyTemplateId: siteTemplateId, category });
    const response = await fetch(`/api/amorgy/global-templates/${globalTemplateId}`,
        {
            method: 'POST',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body
        });
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
        return true;
    else
        return false;
}

export async function editGlobalSurveyTemplate (
    template: GlobalSurveyTemplate,
    onNoAccess: () => void
)
{
    const body = JSON.stringify(template);
    const response = await fetch(`/api/amorgy/global-templates`,
        {
            method: 'PUT',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body
        });
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
        return true;
    else
        return false;
}

export async function getSiteSurveyTemplates (siteId: string, onNoAccess: () => void)
{
    const response = await fetch(`/api/amorgy/site-templates/${siteId}`,
        {
            method: 'GET',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' }
        });
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
        return (await response.json()) as GlobalSurveyTemplate[];
    else
        throw new Error('Unhandled result');
}

export async function login (email: string, password: string)
{
    const response = await fetch(`/api/amorgy/login`,
        {
            method: 'POST',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: `{"email":"${email}", "password":"${password}"}`
        });
    return response.status === 200;
}

export async function logout ()
{
    const response = await fetch(`/api/amorgy/logout`,
        {
            method: 'POST',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' }
        });
    return response.status === 200;
}

export async function getUsers (onNoAccess: () => void)
{
    const response = await fetch(`/api/amorgy/users`,
        {
            method: 'GET',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' }
        });
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
        return (await response.json()) as AmorgAdminUserListing[];
    else
        throw new Error('Unhandled result');
}

export async function createUser (email: string, password: string, onNoAccess: () => void)
{
    const response = await fetch(`/api/amorgy/user`,
        {
            method: 'POST',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: `{"email":"${email}", "password":"${password}"}`
        });
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
        return true;
    else
        return false;
}

export async function changeUserPassword (userId: string, password: string, onNoAccess: () => void)
{
    const response = await fetch(`/api/amorgy/user/${userId}/password`,
        {
            method: 'PUT',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: `{"password":"${password}"}`
        });
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
        return true;
    else
        return false;
}

export async function changeUserAccess (userId: string, enabled: boolean, onNoAccess: () => void)
{
    const response = await fetch(`/api/amorgy/user/${userId}/access`,
        {
            method: 'PUT',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: `{"enabled":${enabled}}`
        });
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
        return true;
    else
        return false;
}

export async function changeUserPartner (userId: string, partner: Partner | null, onNoAccess: () => void)
{
    const response = await fetch(`/api/amorgy/user/${userId}/partner`,
        {
            method: 'PUT',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ partner })
        });
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
        return true;
    else
        return false;
}

export async function getActiveUser (onNoAccess: () => void)
{
    const result = await fetch(`/api/amorgy/activeuser`);
    if (result.status !== 200)
        onNoAccess();

    const token = await result.json();
    return token as AmorgAdminToken;
}

export async function deleteOrganization (organizationId: string, onNoAccess: () => void): Promise<boolean>
{
    const response = await fetch(`/api/amorgy/organizations/${organizationId}`,
        {
            method: 'DELETE',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' }
        });
    if (response.status === 401)
    {
        onNoAccess();
        return false;
    }
    else if (response.status === 200)
        return true;
    else
        return false;
}

function mapOrganizationNoteFromApi (note: OrganizationNote): OrganizationNote
{
    return {
        ...note,
        created: new Date(note.created),
        updated: note.updated ? new Date(note.updated) : null
    };
}

export async function getOrganizationNotes (organizationId: string, onNoAccess: () => void): Promise<OrganizationNote[]>
{
    const response = await fetch(`${amorgAdminPath}/organizations/${organizationId}/notes`);
    if (response.status === 401)
    {
        onNoAccess();
        return [];
    }
    if (response.status !== 200)
        throw new Error('Unhandled result');

    const notes = await response.json() as OrganizationNote[];
    return notes.map(mapOrganizationNoteFromApi);
}

export async function createOrganizationNote (organizationId: string, note: Pick<OrganizationNote, 'flags' | 'note'>, onNoAccess: () => void): Promise<boolean>
{
    const response = await fetch(`${amorgAdminPath}/organizations/${organizationId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(note)
    });
    if (response.status === 401)
    {
        onNoAccess();
        return false;
    }
    return response.status === 200;
}

export async function updateOrganizationNote (organizationId: string, note: OrganizationNote, onNoAccess: () => void): Promise<boolean>
{
    const response = await fetch(`${amorgAdminPath}/organizations/${organizationId}/notes/${note.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(note)
    });
    if (response.status === 401)
    {
        onNoAccess();
        return false;
    }
    return response.status === 200;
}

export async function deleteOrganizationNote (organizationId: string, noteId: string, onNoAccess: () => void): Promise<boolean>
{
    const response = await fetch(`${amorgAdminPath}/organizations/${organizationId}/notes/${noteId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
    });
    if (response.status === 401)
    {
        onNoAccess();
        return false;
    }
    return response.status === 200;
}

export async function uploadUserImportFile (siteId: string, slotId: string, file: File, onNoAccess: () => void): Promise<AmorgAdminHttpCommandResult>
{
    const formData = new FormData();
    formData.append('uploaded_file', file);
    formData.append('slotId', slotId);

    const response = await fetch(`${amorgAdminPath}/sites/${siteId}/import-users/files`,
        {
            method: 'POST',
            cache: 'no-cache',
            body: formData
        });
    if (response.status === 401)
    {
        onNoAccess();
        return { status: 'failure', clientMessage: 'No access' };
    }
    else if (response.status === 200 || response.status === 400)
        return response.json();
    else
        return { status: 'failure', clientMessage: 'Unhandled result' };
}

export async function previewUserImport (siteId: string, slotId: string, onNoAccess: () => void): Promise<UserImportPreviewResult>
{
    const response = await fetch(`${amorgAdminPath}/sites/${siteId}/import-users/preview?slotId=${encodeURIComponent(slotId)}`,
        {
            method: 'GET',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' }
        });
    if (response.status === 401)
    {
        onNoAccess();
        return { status: 'error', clientMessage: 'No access' };
    }
    else if (response.status === 200)
        return response.json();
    else
        return { status: 'error', clientMessage: 'Unhandled result' };
}

export async function importUsers (siteId: string, slotId: string, permissionGroupId: string, password: string, onNoAccess: () => void): Promise<AmorgAdminHttpCommandResult>
{
    const response = await fetch(`${amorgAdminPath}/sites/${siteId}/import-users`,
        {
            method: 'POST',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ slotId, permissionGroupId, password })
        });
    if (response.status === 401)
    {
        onNoAccess();
        return { status: 'failure', clientMessage: 'No access' };
    }
    else if (response.status === 200 || response.status === 400)
        return response.json();
    else
        return { status: 'failure', clientMessage: 'Unhandled result' };
}